import {Container, Graphics, Sprite, Text} from "pixi.js";
import {logger} from "../i.js";
import {getTexture} from "../assets.js";
import {Vector} from "../vector.js";
import {getScreenSize} from "../screen.js";
import {UIUtils} from "../i.js";

const TUTORIALS = new Map([
	["engine", {
		control: "images/icons/controls/right-mouse-button.svg",
		text: "Move",
		stat: "DISTANCE_TRAVELED",
		increase: 5
	}],
	["blaster", {
		control: "images/icons/controls/left-mouse-button.svg",
		text: "Shoot",
		stat: "BLASTER_BOLTS_FIRED",
		increase: 5
	}],
	["nanobots", {
		control: "images/icons/controls/keyboard-e.svg",
		text: "Heal",
		stat: "TIMES_NANOBOTS_USED",
		increase: 1
	}],
	["blade", {
		control: "images/icons/controls/keyboard-space.svg",
		text: "Blade",
		stat: "BLADES_USED",
		increase: 2
	}],
	["thrusters", {
		control: "images/icons/controls/keyboard-asd.svg",
		text: "Maneuver",
		stat: "TIME_MANEUVERING",
		increase: 3
	}],
	["boomdash", {
		control: "images/icons/controls/keyboard-shift.svg",
		text: "Boomdash",
		stat: "BOOMDASHES_USED",
		increase: 3
	}]
]);

const FADE_IN_TIME = 1;
const FADE_OUT_TIME = 1.5;
const REST_TIME = 1.5;

export class Tutorial {
	constructor(dashboard, savefile) {
		this.dashboard = dashboard;

		// If the queue is not empty, the tutorial at [0] is the one currently displayed
		this._queue = savefile?.q ?? ["engine", "blaster"];
		this._completed = savefile?.c ?? [];

		// State of the current tutorial

		// Status of the current tutorial ("fade-in", "stable", "fade-out")
		this._status = null;
		// Timer for fade in / fade out
		this._timer = null;

		// Status of the dock hint
		//
		// null if the hint is not shown. String containing the `canDock` text if the hint
		// is shown. This property is used to only update the dock hint when it actually
		// changes and not every tick.
		this._dockHint = null;

		this.display = new Container();

		this._background = new Graphics();
		this._icon = new Sprite();
		this._text = new Text("", UIUtils.getFont(0xffffff));

		this.display.addChild(this._background, this._icon, this._text);
		this.display.eventMode = "none";

		// Size data
		this._iconHeight = 1;
		// _text.position.x should be round(_icon.width + _textAddX)
		this._textAddX = 1;

		this.resize();
		this._displayNextTutorial(true);
	}

	save() {
		return {
			q: Array.from(this._queue),
			c: Array.from(this._completed)
		};
	}

	resize() {
		let screenSize = getScreenSize();
		let rulerLength = UIUtils.getRulerLength(screenSize);
		let marginWidth = Math.round(0.02 * rulerLength);

		let tutorialSize = new Vector(0.35 * rulerLength, 0.1 * rulerLength).round();
		let tutorialOrigin = new Vector(
			screenSize.x / 2 - tutorialSize.x / 2,
			screenSize.y / 4 - tutorialSize.y / 2
		).round();

		this.display.position.copyFrom(tutorialOrigin);
		this._background
			.clear()
			.roundRect(
				0, 0,
				tutorialSize.x, tutorialSize.y,
				0.025 * rulerLength
			)
			.fill({color: {h: 190, s: 60, v: 25}, alpha: 0.8});

		let iconOrigin = new Vector(marginWidth, marginWidth);
		this._icon.position.copyFrom(iconOrigin);
		this._iconHeight = tutorialSize.y - 2 * marginWidth;
		this._icon.height = this._iconHeight;
		this._icon.scale.x = this._icon.scale.y;

		this._text.style.fontSize = Math.round(0.03 * rulerLength);
		this._textAddX = iconOrigin.x + marginWidth;
		let textOrigin = new Vector(
			this._icon.width + this._textAddX,
			(tutorialSize.y - this._text.height) / 2
		).round();
		this._text.position.copyFrom(textOrigin);
	}

	update(dt) {
		if (!this._queue.length) {
			// No tutorial to display, so we can display the dock hint if needed
			let canDock = this.dashboard.game.space.canDock;
			if (canDock) {
				let dockHintAlreadyCorrect = this._dockHint !== null
					&& this._dockHint === canDock;
				if (dockHintAlreadyCorrect) return;

				this.display.visible = true;
				this._icon.texture = getTexture("images/icons/controls/keyboard-x.svg");
				this._icon.height = this._iconHeight;
				this._icon.scale.x = this._icon.scale.y;
				this._text.text = canDock;
				this._text.position.x = Math.round(this._icon.width + this._textAddX);
				this.display.alpha = 1;

				this._dockHint = canDock;
			} else {
				let dockHintAlreadyCorrect = this._dockHint === null;
				if (dockHintAlreadyCorrect) return;

				this.display.visible = false;

				this._dockHint = null;
			}
			return;
		};

		switch (this._status) {
			case "fade-in": {
				this._statusTimer += dt;
				if (this._statusTimer > FADE_IN_TIME) {
					this._setStatus("stable");
					return;
				}
				this.display.alpha = this._statusTimer / FADE_IN_TIME;
				return;
			}
			case "fade-out": {
				this._statusTimer += dt;
				if (this._statusTimer > FADE_OUT_TIME + REST_TIME) {
					if (this._queue.length === 0) {
						logger.error("tutorial.js", "queue unexpectedly empty");
						return;
					}
					this._completed.push(this._queue.shift());
					this._displayNextTutorial();
					return;
				}
				this.display.alpha = Math.max(
					(FADE_OUT_TIME - this._statusTimer) / FADE_OUT_TIME,
					0
				);
				return;
			}
		}
	}

	// Queue up a tutorial for display
	//
	// Can be called from anywhere.
	//
	// Args:
	//   tutorial - a key from TUTORIALS
	addToQueue(tutorial) {
		if (!TUTORIALS.has(tutorial)) {
			logger.error("tutorial.js", `unknown tutorial: ${tutorial}`);
			return;
		}
		if (this._queue.includes(tutorial) || this._completed.includes(tutorial)) {
			// Some callers may request the same tutorial multiple times, it's fine
			return;
		}
		this._queue.push(tutorial);
		// If the queue was previously empty, display the new tutorial immediately
		if (this._queue.length === 1) this._displayNextTutorial();
	}

	// Display the tutorial at [0] in the queue, or hide the tutorial if queue is empty
	//
	// Args:
	//   instant - if true, the tutorial appears instantly instead of fading in
	_displayNextTutorial(instant) {
		if (this._queue.length === 0) {
			this.display.visible = false;
			return;
		}

		this.display.visible = true;

		let nextTutorial = this._queue[0];

		let info = TUTORIALS.get(nextTutorial);

		this._icon.texture = getTexture(info.control);
		this._icon.height = this._iconHeight;
		this._icon.scale.x = this._icon.scale.y;
		this._text.text = info.text;
		this._text.position.x = Math.round(this._icon.width + this._textAddX);

		this._setStatus(instant ? "stable" : "fade-in");

		this.dashboard.game.unicenter.ship.notifyOnStatIncrease(
			info.stat, info.increase,
			() => this._recordLearning(nextTutorial)
		);

		this._dockHint = null;
	}

	_setStatus(nextStatus) {
		this._status = nextStatus;
		switch (nextStatus) {
			case "fade-in": {
				this._statusTimer = 0;
				this.display.alpha = 0;
				break;
			}
			case "stable": {
				this.display.alpha = 1;
				break;
			}
			case "fade-out": {
				this._statusTimer = 0;
				this.display.alpha = 1;
				break;
			}
		}
	}

	// Record that the player learned the current tutorial
	//
	// Sanity check: `tutorial` must be the currently active tutorial, otherwise this
	// function logs a warning.
	_recordLearning(tutorial) {
		if (this._queue.length === 0 || this._queue[0] !== tutorial) {
			logger.error("tutorial.js",
				"recording the learning of a wrong tutorial: "
				+ `tutorial === ${tutorial}, queue === ${this._queue}`
			);
			// Still proceed, to avoid a tutorial getting stuck forever
		}

		this._setStatus("fade-out");
	}
}
